import React, { useCallback, useEffect, useRef, useState } from "react";

import SmartImage from "../../../components/SmartImage";

const CylinderPlaceholder = () => (
  <span className="sy-gallery-placeholder" aria-hidden="true">
    <svg viewBox="0 0 24 24">
      <path d="M9 2h6v3H9z" />
      <path d="M7.5 5h9a1.5 1.5 0 0 1 1.5 1.5V20a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V6.5A1.5 1.5 0 0 1 7.5 5Z" />
    </svg>
  </span>
);

const Gallery = ({ images, alt, brandMark = false, showThumbs = false }) => {
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);

  const slides = brandMark ? [] : images || [];
  const count = slides.length;

  const goTo = useCallback((next) => {
    const track = trackRef.current;
    if (!track) return;

    const target = track.children[next];
    if (!target) return;

    track.scrollTo({ left: target.offsetLeft, behavior: "smooth" });
    setIndex(next);
  }, []);

  const onScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    const next = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));

    setIndex((current) => (current === next ? current : next));
  }, []);

  const onKeyDown = useCallback(
    (event) => {
      if (event.key === "ArrowRight") {
        event.preventDefault();
        goTo(Math.min(index + 1, count - 1));
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        goTo(Math.max(index - 1, 0));
      }
    },
    [goTo, index, count]
  );

  useEffect(() => {
    setIndex(0);
  }, [count]);

  return (
    <div className={`sy-gallery${showThumbs ? " has-thumbs" : ""}`}>
      <div className={`sy-gallery-main${count ? "" : " is-empty"}`}>
        {count ? (
          <>
            <div
              className="sy-gallery-track"
              ref={trackRef}
              onScroll={onScroll}
              onKeyDown={onKeyDown}
              tabIndex={0}
              role="group"
              aria-label={`${alt} images`}
            >
              {slides.map((src, position) => (
                <div className="sy-gallery-slide" key={src}>
                  <SmartImage
                    src={src}
                    alt={position === 0 ? alt : ""}
                    priority={position === 0}
                  />
                </div>
              ))}
            </div>

            {count > 1 && (
              <>
                <button
                  type="button"
                  className="sy-gallery-nav sy-gallery-prev"
                  onClick={() => goTo(Math.max(index - 1, 0))}
                  disabled={index === 0}
                  aria-label="Previous image"
                >
                  <svg viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M12.5 4 6.5 10l6 6" />
                  </svg>
                </button>

                <button
                  type="button"
                  className="sy-gallery-nav sy-gallery-next"
                  onClick={() => goTo(Math.min(index + 1, count - 1))}
                  disabled={index === count - 1}
                  aria-label="Next image"
                >
                  <svg viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M7.5 4l6 6-6 6" />
                  </svg>
                </button>

                <div className="sy-gallery-dots" role="tablist" aria-label="Images">
                  {slides.map((src, position) => (
                    <button
                      key={src}
                      type="button"
                      role="tab"
                      aria-selected={position === index}
                      aria-label={`Image ${position + 1} of ${count}`}
                      className={`sy-gallery-dot${position === index ? " is-active" : ""}`}
                      onClick={() => goTo(position)}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <CylinderPlaceholder />
        )}
      </div>

      {showThumbs && count > 1 && (
        <div className="sy-gallery-thumbs" role="tablist" aria-label="Product images">
          {slides.map((src, position) => (
            <button
              key={src}
              type="button"
              role="tab"
              aria-selected={position === index}
              aria-label={`Show image ${position + 1}`}
              className={`sy-gallery-thumb${position === index ? " is-active" : ""}`}
              onClick={() => goTo(position)}
            >
              <SmartImage src={src} alt="" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Gallery;
